import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { fieldRegistry } from '../../lib/fieldRegistry';
import type { FieldType } from '../../types/form';

interface DragPreview {
  type: FieldType;
  x: number;
  y: number;
}

export default function DragPreviewPortal() {
  const [preview, setPreview] = useState<DragPreview | null>(null);

  useEffect(() => {
    return monitorForElements({
      onDragStart({ source, location }) {
        // Only show preview for new palette items
        if (source.data.isNew && source.data.fieldType) {
          setPreview({
            type: source.data.fieldType as FieldType,
            x: location.current.input.clientX,
            y: location.current.input.clientY,
          });
        }
      },
      onDrag({ location }) {
        setPreview(prev => {
          if (!prev) return null;
          return {
            ...prev,
            x: location.current.input.clientX,
            y: location.current.input.clientY,
          };
        });
      },
      onDrop() {
        setPreview(null);
      },
    });
  }, []);

  if (!preview) return null;

  const fieldDef = fieldRegistry[preview.type];

  return createPortal(
    <div
      style={{
        position: 'fixed',
        left: preview.x,
        top: preview.y,
        pointerEvents: 'none',
        transform: 'translate(-50%, -50%)',
        zIndex: 9999,
      }}
      className="bg-white dark:bg-gray-800 border-2 border-primary-500 rounded-lg p-3 shadow-xl w-48"
    >
      <div className="flex items-center gap-2">
        <span className="w-8 h-8 flex items-center justify-center bg-primary-100 dark:bg-primary-900 text-primary-600 dark:text-primary-400 rounded text-base">
          {fieldDef.icon}
        </span>
        <span className="font-medium text-gray-900 dark:text-white text-sm">
          {fieldDef.label}
        </span>
      </div>
    </div>,
    document.body
  );
}
